/**
 * PackageChat - Full-featured real-time chat with WebRTC calls
 * Features: WebSocket messaging, video/voice calls, reactions, file attachments, search
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Send, X, MessageCircle, User, Truck, Package, Loader2,
  ChevronDown, CheckCheck, MapPin, Paperclip, FileText,
  Navigation, XCircle, Phone, Video, PhoneOff, VideoOff,
  Mic, MicOff, Search, Smile, Download
} from 'lucide-react';

const ROLE_STYLES = {
  sender: { bg: 'bg-emerald-500', icon: User },
  recipient: { bg: 'bg-blue-500', icon: Package },
  driver: { bg: 'bg-amber-500', icon: Truck }
};

const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏', '✅', '🔥'];
const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const WS_BASE = API_BASE.replace('https://', 'wss://').replace('http://', 'ws://') || 'ws://localhost:8001';

export default function PackageChat({
  trackingCode,
  chatType = 'sender_recipient',
  onClose,
  floating = true,
  className = ''
}) {
  const { user, token } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [driverLocation, setDriverLocation] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  
  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Reaction picker state
  const [showReactions, setShowReactions] = useState(null); // message id
  
  // Call state
  const [inCall, setInCall] = useState(false);
  const [callType, setCallType] = useState(null);
  const [incomingCall, setIncomingCall] = useState(null);
  const [showCallConfirm, setShowCallConfirm] = useState(null); // audio or video
  const [callState, setCallState] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const callWsRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize chat
  const initChat = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.post('/package-chat/start', {
        tracking_code: trackingCode,
        chat_type: chatType
      });
      setConversation(res.data.conversation);
      
      if (res.data.conversation?.id) {
        const msgRes = await api.get(`/package-chat/${res.data.conversation.id}/messages`);
        setMessages(msgRes.data.messages || []);
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start chat');
    } finally {
      setLoading(false);
    }
  }, [trackingCode, chatType]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!conversation?.id || !token || wsRef.current?.readyState === WebSocket.OPEN) return;

    const wsUrl = `${WS_BASE}/ws/package-chat/${conversation.id}?token=${token}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setWsConnected(true);
      };
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        handleWsMessage(data);
      };
      
      wsRef.current.onclose = () => {
        setWsConnected(false);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (conversation?.id) connectWebSocket();
        }, 3000);
      };
    } catch (error) {
      console.error('WebSocket connection failed:', error);
    }
  }, [conversation?.id, token]);

  const handleWsMessage = (data) => {
    switch (data.type) {
      case 'connected':
        setOnlineUsers(data.online_users || []);
        if (data.driver_location) setDriverLocation(data.driver_location);
        break;
      case 'new_message':
        setMessages(prev => [...prev, data.message]);
        scrollToBottom();
        break;
      case 'message_sent':
        setMessages(prev => prev.map(m => m.pending ? data.message : m));
        break;
      case 'typing':
        if (data.is_typing) {
          setTypingUsers(prev => ({...prev, [data.user_id]: data.user_name}));
        } else {
          setTypingUsers(prev => { const u = {...prev}; delete u[data.user_id]; return u; });
        }
        break;
      case 'user_joined':
      case 'user_left':
        setOnlineUsers(data.online_users || []);
        break;
      case 'driver_location':
        setDriverLocation({ lat: data.lat, lng: data.lng, driver_name: data.driver_name, updated_at: data.updated_at });
        break;
      case 'incoming_call':
        setIncomingCall(data);
        break;
      case 'call_ended':
        handleCallEnded();
        break;
      case 'reaction_update':
        setMessages(prev => prev.map(m => m.id === data.message_id ? {...m, reactions: data.reactions} : m));
        break;
    }
  };

  useEffect(() => {
    if (trackingCode && user) initChat();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      endCall();
    };
  }, [trackingCode, user, initChat]);

  useEffect(() => {
    if (conversation?.id && token && !isMinimized) connectWebSocket();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [conversation?.id, token, isMinimized, connectWebSocket]);

  // Search functionality
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await api.get(`/package-chat/${conversation.id}/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data.messages || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  const debouncedSearch = (query) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => handleSearch(query), 300);
  };

  // Reactions
  const handleReaction = async (messageId, emoji) => {
    try {
      const res = await api.post(`/package-chat/${conversation.id}/messages/${messageId}/react`, { emoji });
      setMessages(prev => prev.map(m => m.id === messageId ? {...m, reactions: res.data.reactions} : m));
      setShowReactions(null);
    } catch (error) {
      toast.error('Failed to add reaction');
    }
  };

  // Voice/Video calling
  const startCall = async (type) => {
    try {
      const res = await api.post(`/package-chat/${conversation.id}/call/initiate?call_type=${type}&token=${token}`);
      setCallType(type);
      setInCall(true);
      setCallState('calling');
      
      // Get media stream
      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === 'video',
        audio: true
      });
      setLocalStream(stream);
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      
      // Connect to signaling WebSocket
      connectCallWebSocket(res.data.call_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start call');
      setInCall(false);
    }
  };

  const answerCall = async () => {
    if (!incomingCall) return;
    
    try {
      await api.post(`/package-chat/call/${incomingCall.call_id}/answer?token=${token}`);
      setCallType(incomingCall.call_type);
      setInCall(true);
      setCallState('connected');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: incomingCall.call_type === 'video',
        audio: true
      });
      setLocalStream(stream);
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      
      connectCallWebSocket(incomingCall.call_id);
      setIncomingCall(null);
    } catch (error) {
      toast.error('Failed to answer call');
    }
  };

  const rejectCall = async () => {
    if (!incomingCall) return;
    try {
      await api.post(`/package-chat/call/${incomingCall.call_id}/reject?token=${token}`);
    } catch {}
    setIncomingCall(null);
  };

  const endCall = async () => {
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (callWsRef.current) {
      callWsRef.current.close();
      callWsRef.current = null;
    }
    setInCall(false);
    setCallState(null);
    setRemoteStream(null);
  };

  const handleCallEnded = () => {
    endCall();
    toast.info('Call ended');
  };

  const connectCallWebSocket = (callId) => {
    const wsUrl = `${WS_BASE}/package-chat/ws/call/${callId}?token=${token}`;
    callWsRef.current = new WebSocket(wsUrl);
    
    callWsRef.current.onopen = () => {
      initializePeerConnection();
    };
    
    callWsRef.current.onmessage = async (event) => {
      const data = JSON.parse(event.data);
      await handleCallSignaling(data);
    };
  };

  const initializePeerConnection = async () => {
    // Fetch dynamic call config with multiple ICE servers for better connectivity
    let config = { 
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    };
    
    try {
      const quality = callType === 'audio' ? 'audio_only' : 'medium';
      const configRes = await fetch(`${API_BASE}/api/package-chat/config/webrtc?quality=${quality}`);
      if (configRes.ok) {
        const data = await configRes.json();
        config = {
          iceServers: data.iceServers,
          sdpSemantics: data.sdpSemantics || 'unified-plan',
          bundlePolicy: data.bundlePolicy || 'max-bundle'
        };
      }
    } catch (err) {
      console.warn('Using default ICE config:', err);
    }
    
    peerConnectionRef.current = new RTCPeerConnection(config);
    
    localStream?.getTracks().forEach(track => {
      peerConnectionRef.current.addTrack(track, localStream);
    });
    
    peerConnectionRef.current.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = event.streams[0];
    };
    
    peerConnectionRef.current.onicecandidate = (event) => {
      if (event.candidate && callWsRef.current?.readyState === WebSocket.OPEN) {
        callWsRef.current.send(JSON.stringify({ type: 'ice_candidate', candidate: event.candidate }));
      }
    };
    
    // Create offer if we initiated
    if (callState === 'calling') {
      createOffer();
    }
  };

  const createOffer = async () => {
    const offer = await peerConnectionRef.current.createOffer();
    await peerConnectionRef.current.setLocalDescription(offer);
    callWsRef.current.send(JSON.stringify({ type: 'offer', sdp: offer.sdp }));
  };

  const handleCallSignaling = async (data) => {
    if (!peerConnectionRef.current) return;
    
    if (data.type === 'offer') {
      await peerConnectionRef.current.setRemoteDescription({ type: 'offer', sdp: data.sdp });
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);
      callWsRef.current.send(JSON.stringify({ type: 'answer', sdp: answer.sdp }));
      setCallState('connected');
    } else if (data.type === 'answer') {
      await peerConnectionRef.current.setRemoteDescription({ type: 'answer', sdp: data.sdp });
      setCallState('connected');
    } else if (data.type === 'ice_candidate') {
      await peerConnectionRef.current.addIceCandidate(data.candidate);
    } else if (data.type === 'call_ended') {
      handleCallEnded();
    }
  };

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach(track => track.enabled = isMuted);
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (localStream) {
      localStream.getVideoTracks().forEach(track => track.enabled = isVideoOff);
      setIsVideoOff(!isVideoOff);
    }
  };

  // Message sending
  const sendTyping = (isTyping) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: isTyping }));
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    sendTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => sendTyping(false), 2000);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post(`/package-chat/upload?conversation_id=${conversation.id}`, formData, 
          { headers: { 'Content-Type': 'multipart/form-data' } });
        setAttachments(prev => [...prev, res.data.attachment]);
      } catch { toast.error(`Failed to upload ${file.name}`); }
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && !attachments.length) || !conversation?.id) return;
    
    const text = newMessage.trim();
    const atts = [...attachments];
    setNewMessage('');
    setAttachments([]);
    sendTyping(false);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const optimistic = { id: 'temp-' + Date.now(), sender_id: user.id, sender_name: user.name, sender_role: 'sender', text, attachments: atts, created_at: new Date().toISOString(), is_own: true, pending: true };
      setMessages(prev => [...prev, optimistic]);
      scrollToBottom();
      wsRef.current.send(JSON.stringify({ type: 'send_message', text, attachments: atts }));
    } else {
      setSending(true);
      try {
        const res = await api.post(`/package-chat/${conversation.id}/send`, { text, attachments: atts });
        setMessages(prev => [...prev, res.data.message]);
        scrollToBottom();
      } catch { toast.error('Failed to send'); setNewMessage(text); setAttachments(atts); }
      setSending(false);
    }
  };

  

  // Export chat history
  const exportChat = async (format = "txt") => {
    try {
      const res = await api.get(`/package-chat/${conversation.id}/export?format=${format}`);
      const { content: fileContent, filename } = res.data;
      
      const blob = new Blob([typeof fileContent === "object" ? JSON.stringify(fileContent, null, 2) : fileContent], 
        { type: format === "json" ? "application/json" : "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Chat exported!");
    } catch (error) {
      toast.error("Failed to export chat");
    }
  };

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const getRoleStyle = (role) => ROLE_STYLES[role] || ROLE_STYLES.sender;
  const typingNames = Object.values(typingUsers);

  // Render call UI
  if (inCall) {
    return (
      <div className={`${floating ? 'fixed bottom-4 right-4 w-80 md:w-96 z-50' : 'w-full'} ${className}`}>
        <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
          <div className="relative aspect-video bg-gray-800">
            {remoteStream && <video ref={remoteVideoRef} autoPlay playsInline className="w-full h-full object-cover" />}
            {!remoteStream && <div className="w-full h-full flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white" /><span className="ml-2 text-white">{callState === 'calling' ? 'Calling...' : 'Connecting...'}</span></div>}
            {localStream && callType === 'video' && (
              <div className="absolute bottom-2 right-2 w-24 h-18 rounded-lg overflow-hidden border-2 border-white">
                <video ref={localVideoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          <div className="p-4 flex justify-center gap-4">
            <button onClick={toggleMute} className={`p-3 rounded-full ${isMuted ? 'bg-red-500' : 'bg-gray-700'} text-white`}>
              {isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </button>
            {callType === 'video' && (
              <button onClick={toggleVideo} className={`p-3 rounded-full ${isVideoOff ? 'bg-red-500' : 'bg-gray-700'} text-white`}>
                {isVideoOff ? <VideoOff className="h-5 w-5" /> : <Video className="h-5 w-5" />}
              </button>
            )}
            <button onClick={endCall} className="p-3 rounded-full bg-red-500 text-white">
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render call confirmation dialog
  if (showCallConfirm) {
    return (
      <div className={`${floating ? "fixed bottom-4 right-4 w-80 z-50" : "w-full"} ${className}`}>
        <div className="bg-white rounded-2xl shadow-2xl border p-6 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {showCallConfirm === "video" ? <Video className="h-8 w-8 text-blue-600" /> : <Phone className="h-8 w-8 text-blue-600" />}
          </div>
          <h3 className="font-bold text-lg mb-2">Start {showCallConfirm === "video" ? "Video" : "Voice"} Call?</h3>
          <p className="text-gray-500 text-sm mb-4">Would you like to call or continue chatting?</p>
          <div className="space-y-2">
            <Button 
              onClick={() => { startCall(showCallConfirm); setShowCallConfirm(null); }} 
              className="w-full bg-emerald-500 hover:bg-emerald-600"
            >
              <Phone className="h-4 w-4 mr-2" />
              Start {showCallConfirm === "video" ? "Video" : "Voice"} Call
            </Button>
            <Button 
              onClick={() => setShowCallConfirm(null)} 
              variant="outline" 
              className="w-full"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Continue Chatting
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-3">Calls are recorded for 15 days for dispute resolution</p>
        </div>
      </div>
    );
  }

  // Render incoming call
  if (incomingCall) {
    return (
      <div className={`${floating ? 'fixed bottom-4 right-4 w-80 z-50' : 'w-full'} ${className}`}>
        <div className="bg-white rounded-2xl shadow-2xl border p-6 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            {incomingCall.call_type === 'video' ? <Video className="h-8 w-8 text-emerald-600" /> : <Phone className="h-8 w-8 text-emerald-600" />}
          </div>
          <h3 className="font-bold text-lg">{incomingCall.caller_name}</h3>
          <p className="text-gray-500 text-sm">Incoming {incomingCall.call_type} call</p>
          <div className="flex gap-3 mt-4">
            <Button onClick={rejectCall} variant="outline" className="flex-1 border-red-500 text-red-500"><PhoneOff className="h-4 w-4 mr-1" />Decline</Button>
            <Button onClick={answerCall} className="flex-1 bg-emerald-500"><Phone className="h-4 w-4 mr-1" />Answer</Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className={`${floating ? 'fixed bottom-4 right-4 w-80 bg-white rounded-2xl shadow-2xl border' : ''} ${className}`}><div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div></div>;

  if (isMinimized) return (
    <button onClick={() => setIsMinimized(false)} className="fixed bottom-4 right-4 bg-emerald-500 text-white p-4 rounded-full shadow-lg hover:bg-emerald-600 z-50">
      <MessageCircle className="h-6 w-6" />
    </button>
  );

  return (
    <div className={`${floating ? 'fixed bottom-4 right-4 w-80 md:w-96 z-50' : 'w-full'} ${className}`}>
      <div className="bg-white rounded-2xl shadow-2xl border overflow-hidden flex flex-col" style={{ maxHeight: floating ? '550px' : '600px' }}>
        {/* Header */}
        <div className="bg-emerald-500 text-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-2 rounded-full relative">
                <MessageCircle className="h-5 w-5" />
                {wsConnected && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-emerald-500" />}
              </div>
              <div>
                <h3 className="font-semibold text-sm">{trackingCode}</h3>
                <span className="text-xs text-white/80">{onlineUsers.length} online</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => startCall('audio')} className="p-1.5 hover:bg-white/20 rounded-full" title="Voice Call"><Phone className="h-4 w-4" /></button>
              <button onClick={() => startCall('video')} className="p-1.5 hover:bg-white/20 rounded-full" title="Video Call"><Video className="h-4 w-4" /></button>
              <button onClick={() => setShowSearch(!showSearch)} className="p-1.5 hover:bg-white/20 rounded-full" title="Search"><Search className="h-4 w-4" /></button>
              <button onClick={() => exportChat("txt")} className="p-1.5 hover:bg-white/20 rounded-full" title="Export Chat"><Download className="h-4 w-4" /></button>
              <button onClick={() => setIsMinimized(true)} className="p-1.5 hover:bg-white/20 rounded-full"><ChevronDown className="h-5 w-5" /></button>
              {onClose && <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-full"><X className="h-5 w-5" /></button>}
            </div>
          </div>
          
          {showSearch && (
            <div className="mt-2">
              <Input value={searchQuery} onChange={(e) => debouncedSearch(e.target.value)} placeholder="Search messages..." className="bg-white/20 border-0 text-white placeholder:text-white/60 h-8 text-sm" />
            </div>
          )}
          
          {driverLocation && (
            <div className="mt-2 bg-white/10 rounded-lg px-3 py-2 flex items-center gap-2">
              <Navigation className="h-4 w-4 text-amber-300" />
              <span className="text-xs flex-1">{driverLocation.driver_name}</span>
              <button onClick={() => window.open(`https://maps.google.com/?q=${driverLocation.lat},${driverLocation.lng}`, '_blank')} className="text-xs bg-white/20 px-2 py-0.5 rounded">Map</button>
            </div>
          )}
        </div>

        {/* Messages or Search Results */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50" style={{ minHeight: '200px' }}>
          {showSearch && searchQuery ? (
            searching ? <div className="text-center py-4"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> :
            searchResults.length === 0 ? <p className="text-center text-gray-500 py-4">No results</p> :
            searchResults.map(msg => (
              <div key={msg.id} className="bg-white p-3 rounded-lg border text-sm">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span>{msg.sender_name}</span><span>{formatTime(msg.created_at)}</span>
                </div>
                <p dangerouslySetInnerHTML={{ __html: msg.highlighted_text?.replace(/\*\*(.*?)\*\*/g, '<mark class="bg-yellow-200"></mark>') || msg.text }} />
              </div>
            ))
          ) : messages.length === 0 ? (
            <div className="text-center py-8"><MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 text-sm">No messages yet</p></div>
          ) : (
            messages.map((msg) => {
              const style = getRoleStyle(msg.sender_role);
              const Icon = style.icon;
              return (
                <div key={msg.id} className={`flex ${msg.is_own ? 'justify-end' : 'justify-start'} group`}>
                  <div className="max-w-[80%] relative">
                    {!msg.is_own && (
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className={`w-5 h-5 rounded-full ${style.bg} flex items-center justify-center`}><Icon className="h-3 w-3 text-white" /></div>
                        <span className="text-xs text-gray-500">{msg.sender_name}</span>
                      </div>
                    )}
                    <div className={`px-3 py-2 rounded-2xl ${msg.is_own ? 'bg-emerald-500 text-white rounded-br-md' : 'bg-white text-gray-800 border rounded-bl-md shadow-sm'} ${msg.pending ? 'opacity-70' : ''}`}>
                      {msg.text && <p className="text-sm whitespace-pre-wrap">{msg.text}</p>}
                      {msg.attachments?.map((att, i) => (
                        <a key={i} href={process.env.REACT_APP_BACKEND_URL + att.url} target="_blank" rel="noreferrer" className="block mt-2">
                          {att.type === 'image' ? <img src={process.env.REACT_APP_BACKEND_URL + att.url} alt="" className="max-w-full rounded-lg" /> : 
                          <div className="flex items-center gap-2 p-2 bg-white/20 rounded"><FileText className="h-4 w-4" /><span className="text-xs">{att.name}</span></div>}
                        </a>
                      ))}
                    </div>
                    
                    {/* Reactions */}
                    {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Object.entries(msg.reactions).map(([emoji, users]) => (
                          <button key={emoji} onClick={() => handleReaction(msg.id, emoji)} className="bg-gray-100 hover:bg-gray-200 rounded-full px-2 py-0.5 text-xs flex items-center gap-1">
                            {emoji}<span className="text-gray-500">{users.length}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Reaction button */}
                    <button onClick={() => setShowReactions(showReactions === msg.id ? null : msg.id)} className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-100 rounded">
                      <Smile className="h-4 w-4 text-gray-400" />
                    </button>
                    
                    {/* Reaction picker */}
                    {showReactions === msg.id && (
                      <div className="absolute -left-2 -top-10 bg-white rounded-full shadow-lg border p-1 flex gap-1 z-10">
                        {REACTIONS.map(emoji => (
                          <button key={emoji} onClick={() => handleReaction(msg.id, emoji)} className="hover:bg-gray-100 rounded-full p-1 text-lg">{emoji}</button>
                        ))}
                      </div>
                    )}
                    
                    <div className={`flex items-center gap-1 mt-1 ${msg.is_own ? 'justify-end' : ''}`}>
                      <span className="text-xs text-gray-400">{formatTime(msg.created_at)}</span>
                      {msg.is_own && !msg.pending && (
                        <span className="flex items-center" title={msg.read_by?.length > 1 ? `Read by ${msg.read_by.length - 1} recipient(s)` : "Delivered"}>
                          {msg.read_by?.length > 1 ? (
                            <CheckCheck className="h-3 w-3 text-blue-500" />
                          ) : (
                            <Check className="h-3 w-3 text-gray-400" />
                          )}
                        </span>
                      )}
                      {msg.pending && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          
          {typingNames.length > 0 && (
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" /><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'150ms'}} /><span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay:'300ms'}} />
              </div>
              <span>{typingNames.join(', ')} typing...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="px-3 py-2 bg-gray-100 border-t flex gap-2 overflow-x-auto">
            {attachments.map((att, i) => (
              <div key={i} className="relative flex-shrink-0">
                {att.type === 'image' ? <img src={process.env.REACT_APP_BACKEND_URL + att.url} alt="" className="h-16 w-16 object-cover rounded-lg" /> : 
                <div className="h-16 w-16 bg-white rounded-lg flex flex-col items-center justify-center border"><FileText className="h-6 w-6 text-gray-400" /></div>}
                <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"><XCircle className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="p-3 bg-white border-t">
          <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2 items-end">
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*,.pdf,.doc,.docx" multiple className="hidden" />
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="p-2 text-gray-400 hover:text-emerald-500 hover:bg-gray-100 rounded-full">
              {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
            </button>
            <Input value={newMessage} onChange={handleInputChange} placeholder="Type a message..." className="flex-1 rounded-full" disabled={sending} />
            <Button type="submit" disabled={(!newMessage.trim() && !attachments.length) || sending} className="bg-emerald-500 hover:bg-emerald-600 rounded-full w-10 h-10 p-0">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
